import { useEffect, useMemo, useState } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { PlainOpeningHoursDto } from '@ordr-as/menu-builder-types/epos';
import type { SectionNextOpen } from '../utils/openingHours';
import { findSectionNextOpen, isWithinOpeningHours } from '../utils/openingHours';
import { useLocation } from '../context/LocationContext';

interface OpeningHoursActions {
  setAllOpeningHours: (data: Record<string, PlainOpeningHoursDto[]>) => void;
  setServerTimeOffset: (offsetMs: number) => void;
  clear: () => void;
}

export interface OpeningHoursState {
  // Opening hours indexed by sectionId for future cart validation
  sectionOpeningHours: Record<string, PlainOpeningHoursDto[]>;
  serverTimeOffsetMs: number;
  actions: OpeningHoursActions;
}

export const useOpeningHoursStore = create<OpeningHoursState>()((set) => ({
  actions: {
    clear: () => set({ sectionOpeningHours: {}, serverTimeOffsetMs: 0 }),
    setAllOpeningHours: (data) => set({ sectionOpeningHours: data }),
    setServerTimeOffset: (offsetMs) => set({ serverTimeOffsetMs: offsetMs }),
  },
  sectionOpeningHours: {},
  serverTimeOffsetMs: 0,
}));

export const useSectionOpeningHours = (sectionId: string) =>
  useOpeningHoursStore((state) => state.sectionOpeningHours[sectionId] ?? []);

export const useAllOpeningHours = () => useOpeningHoursStore((state) => state.sectionOpeningHours);
export const useOpeningHoursActions = () => useOpeningHoursStore((state) => state.actions);
export const useServerTimeOffsetMs = () => useOpeningHoursStore((state) => state.serverTimeOffsetMs);

function useMinuteTick(): number {
  const [tick, setTick] = useState(() => Math.floor(Date.now() / 60_000));

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(Math.floor(Date.now() / 60_000));
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  return tick;
}

export const useSectionNextOpen = (zoneId: string, sectionId?: string): SectionNextOpen | null => {
  const hours = useOpeningHoursStore((state) => (sectionId ? state.sectionOpeningHours[sectionId] ?? [] : []));
  const { location } = useLocation();
  const locationHours = location.openingHours;
  const serverTimeOffsetMs = useServerTimeOffsetMs();
  const minuteTick = useMinuteTick();

  return useMemo(() => {
    if (hours.length === 0) {
      return null;
    }
    const now = new Date(Date.now() + serverTimeOffsetMs);
    if (isWithinOpeningHours(hours, now, zoneId)) {
      return null;
    }
    return findSectionNextOpen(hours, locationHours, now, zoneId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, locationHours, zoneId, minuteTick, serverTimeOffsetMs]);
};
